import { sanitizeTickerFilterValue } from "../shared/tickerFilterInput";
import { normalizeTicker } from "./watchlistData";

export type WheelTickerFilterTarget = "options" | "puts" | "calls" | "stocks" | "history";

export const WHEEL_TICKER_FILTER_EVENT = "sfe-wheel-ticker-filter";

const STORAGE_KEYS: Record<WheelTickerFilterTarget, string> = {
  options: "sfe-web-options-filters-v1",
  puts: "sfe-web-put-filters-v1",
  calls: "sfe-web-call-filters-v1",
  stocks: "sfe-web-stocks-filters-v1",
  history: "sfe-web-history-filters-v1",
};

const INPUT_IDS: Record<WheelTickerFilterTarget, string> = {
  options: "wheelOptTicker",
  puts: "wheelPutTicker",
  calls: "wheelCallTicker",
  stocks: "wheelStockTicker",
  history: "wheelHistTicker",
};

function patchStoredTicker(key: string, ticker: string): void {
  try {
    const raw = localStorage.getItem(key);
    const base = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    base.ticker = ticker;
    localStorage.setItem(key, JSON.stringify(base));
  } catch {
    localStorage.setItem(key, JSON.stringify({ ticker }));
  }
}

export function readWheelTabTickerFilter(target: WheelTickerFilterTarget): string {
  const input = document.getElementById(INPUT_IDS[target]);
  if (input instanceof HTMLInputElement) {
    return sanitizeTickerFilterValue(input.value).toUpperCase();
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEYS[target]);
    if (!raw) return "";
    const parsed = JSON.parse(raw) as { ticker?: string };
    return sanitizeTickerFilterValue(String(parsed.ticker ?? "")).toUpperCase();
  } catch {
    return "";
  }
}

export function clearWheelTabTickerFilter(target: WheelTickerFilterTarget): boolean {
  patchStoredTicker(STORAGE_KEYS[target], "");

  const input = document.getElementById(INPUT_IDS[target]);
  if (input instanceof HTMLInputElement) {
    input.value = "";
    input.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  }

  document.dispatchEvent(
    new CustomEvent(WHEEL_TICKER_FILTER_EVENT, {
      detail: { target, ticker: "" },
    }),
  );
  return true;
}

export function isWheelTabTickerFilterActive(
  target: WheelTickerFilterTarget,
  ticker: string,
): boolean {
  const normalized = sanitizeTickerFilterValue(normalizeTicker(ticker)).toUpperCase();
  if (!normalized) return false;
  return readWheelTabTickerFilter(target) === normalized;
}

export function wheelTargetForRecSide(side: string | null | undefined): "puts" | "calls" {
  return String(side || "").toLowerCase().startsWith("c") ? "calls" : "puts";
}

/** Prefix-match ticker filter for a wheel tab; updates storage and live filter input when present. */
export function applyWheelTabTickerFilter(target: WheelTickerFilterTarget, ticker: string): boolean {
  const normalized = sanitizeTickerFilterValue(normalizeTicker(ticker)).toUpperCase();
  if (!normalized) return false;

  patchStoredTicker(STORAGE_KEYS[target], normalized);

  const input = document.getElementById(INPUT_IDS[target]);
  if (input instanceof HTMLInputElement) {
    input.value = normalized;
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.focus();
    input.closest(".filter-bar")?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    return true;
  }

  document.dispatchEvent(
    new CustomEvent(WHEEL_TICKER_FILTER_EVENT, {
      detail: { target, ticker: normalized },
    }),
  );
  return true;
}

/** Apply filter for ticker, or clear when that ticker is already the active filter. Returns true if filter is on after. */
export function toggleWheelTabTickerFilter(
  target: WheelTickerFilterTarget,
  ticker: string,
): boolean {
  if (isWheelTabTickerFilterActive(target, ticker)) {
    clearWheelTabTickerFilter(target);
    return false;
  }
  applyWheelTabTickerFilter(target, ticker);
  return true;
}
